import React, { useCallback, useRef } from 'react'

/**
 * Card that tracks the pointer in 3D. Falls back to the CSS-only `.tilt`
 * hover when there is no fine pointer, and disables entirely under
 * prefers-reduced-motion.
 *
 * Writes transforms straight to the node inside rAF rather than through
 * state — a re-render per mousemove is what makes this kind of effect feel
 * cheap.
 */
export default function TiltCard({
  as: Tag = 'div',
  className = '',
  intensity = 5,
  children,
  ...rest
}) {
  const ref = useRef(null)
  const frame = useRef(0)

  const allowed = useCallback(() => {
    if (typeof window === 'undefined') return false
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
    return window.matchMedia('(pointer: fine)').matches
  }, [])

  const handleMove = useCallback(
    (e) => {
      const el = ref.current
      if (!el || !allowed()) return

      const rect = el.getBoundingClientRect()
      // -0.5 .. 0.5 from the card's centre.
      const px = (e.clientX - rect.left) / rect.width - 0.5
      const py = (e.clientY - rect.top) / rect.height - 0.5

      cancelAnimationFrame(frame.current)
      frame.current = requestAnimationFrame(() => {
        el.style.transform =
          `rotateX(${(-py * intensity).toFixed(2)}deg) ` +
          `rotateY(${(px * intensity).toFixed(2)}deg) ` +
          `translateZ(16px)`
      })
    },
    [allowed, intensity]
  )

  const handleLeave = useCallback(() => {
    const el = ref.current
    if (!el) return
    cancelAnimationFrame(frame.current)
    el.style.transform = ''
  }, [])

  return (
    <Tag
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`tilt ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  )
}
